import UserLoggingService from '~/services/user-logging.service'
import isDescopeMfaEnabled from '~/composables/use-descope-mfa-flag'

const userLoggingService = new UserLoggingService()

/*
 * Every MFA step the FE reports to Member Hub's auth_logs. Server-side events
 * (mfa_enabled / mfa_disabled, logged from UserRepository::updateProfile on a real flag
 * transition) are deliberately absent — the FE must never claim a transition it cannot
 * know happened.
 */
export const MFA_EVENT = {
  ENROLMENT_STARTED: 'mfa_enrolment_started',
  CHALLENGE_SHOWN: 'mfa_challenge_shown',
  CODE_SENT: 'mfa_code_sent',
  CODE_SEND_FAILED: 'mfa_code_send_failed',
  SEND_RATE_LIMITED: 'mfa_send_rate_limited',
  CHALLENGE_SUCCEEDED: 'mfa_challenge_succeeded',
  CHALLENGE_FAILED: 'mfa_challenge_failed',
  MAX_ATTEMPTS_EXCEEDED: 'mfa_max_attempts_exceeded',
  CHALLENGE_ABANDONED: 'mfa_challenge_abandoned',
  DISABLE_FAILED: 'mfa_disable_failed',
  // A bypass_MFA test account skipped MFA (CLUB-950). Member Hub only accepts it from an
  // account it actually exempts.
  BYPASSED: 'mfa_bypassed',
  /*
   * Lost-phone recovery milestones — only the four with no structural equivalent above.
   * Recovery's code sends, rate limits, wrong codes and exhausted attempts reuse the
   * events above, told apart by a RECOVERY_* context.
   */
  RECOVERY_STARTED: 'mfa_recovery_started',
  RECOVERY_EMAIL_VERIFIED: 'mfa_recovery_email_verified',
  RECOVERY_COMPLETED: 'mfa_recovery_completed',
  RECOVERY_ABANDONED: 'mfa_recovery_abandoned'
}

/*
 * Which surface raised the event. The same challenge events fire from several different
 * places, and without this they are indistinguishable in the log.
 *
 * Recovery needs two contexts rather than one: it runs two one-time-code stages back to
 * back (email, then the replacement number), both reusing the same send/verify/fail
 * events, so a single context could not say which stage a failure came from.
 */
export const MFA_CONTEXT = {
  LOGIN_CHALLENGE: 'login_challenge',
  ENROLMENT: 'enrolment',
  REAUTH: 'reauth',
  SETTINGS: 'settings',
  RECOVERY_EMAIL: 'recovery_email',
  RECOVERY_PHONE: 'recovery_phone'
}

/*
 * How a code send was triggered — the first automatic send on landing, or the user
 * pressing Resend. Resend rate is the main signal for "the SMS never arrived".
 */
export const MFA_TRIGGER = {
  AUTO: 'auto',
  RESEND: 'resend'
}

/*
 * Fire-and-forget journey logging for the MFA flows.
 *
 * Three hard rules, because this is observability and must never become a failure mode of
 * the thing it observes:
 *
 *  1. NON-BLOCKING. logMfaEvent() returns undefined, not a promise, so there is nothing a
 *     caller can accidentally `await`. The request is started on a detached promise chain
 *     and the caller continues immediately.
 *  2. NEVER THROWS. Every failure — flag lookup, network, 4xx/5xx, a missing endpoint
 *     before CLUB-821 ships — is swallowed. A user must never see a broken login because
 *     a log line did not land.
 *  3. NO PII. Only the event name, context and coarse non-identifying detail. Never email,
 *     phone number, OTP or token in the body; Member Hub derives identity from the bearer
 *     token. See agent-os/standards/nuxt/logging.md.
 *
 * Gated on the same feature_DescopeMFA flag as every other MFA surface, so the calls
 * disappear entirely when the feature is off.
 */
export default function useMfaLogging () {
  /*
     * @param event - one of MFA_EVENT
     * @param options - { context, token, ...detail }
     *        context: one of MFA_CONTEXT — which surface this came from.
     *        token:   access token for surfaces whose session is suspended (the challenge
     *                 and enrolment pages). Stripped from the body; sent as a header only.
     *        detail:  any other non-PII fields, e.g. { trigger, failure_reason }.
     * @returns {void} — deliberately not a promise; see rule 1 above.
     */
  function logMfaEvent (event, options = {}) {
    // The stored token, the flag client and the endpoint are all browser-side. On the
    // server there is nothing to authenticate with, so skip rather than log anonymously.
    if (!import.meta.client || !event) return

    const { token, ...detail } = options

    Promise.resolve()
      .then(async () => {
        if (!(await isDescopeMfaEnabled())) return
        await userLoggingService.logEvent({ event, ...detail }, token)
      })
      .catch(() => {
        // Swallowed by design — see rule 2. Reporting a failed log line to Sentry would
        // turn one broken endpoint into an alert storm across every login.
      })
  }

  return { logMfaEvent }
}
